import { useMemo } from 'react';
import { useAuth } from './useAuth';

/**
 * Returns `true` when the current user has platform `admin` or any of the
 * supplied domain-specific admin roles. Mirrors the server-side
 * `requireAdminOrLitcraftAdmin` (and analogous) middlewares so the client
 * UI gate stays in sync with route authorization automatically — the bug
 * pattern we keep hitting is a client check that admits only `'admin'`
 * while the route accepts `'admin' || 'litcraft_admin'` (or vice-versa).
 *
 * Usage:
 * ```ts
 * // Game-related arche admin entry-point: matches the server-side
 * // `requireAdminOrLitcraftAdmin` middleware on the route.
 * const isAdmin = useArcheAdmin('litcraft_admin');
 *
 * // Platform-only feature (no extra roles): explicit empty list.
 * const isAdmin = useArcheAdmin();
 * ```
 *
 * **Important:** platform `admin` is always accepted regardless of the
 * `domainRoles` passed in — that matches the server, where every
 * `requireAdmin*` family middleware lets a platform admin through. If you
 * want a check that *excludes* platform admin (rare), you need a different
 * hook.
 *
 * Pass nothing (or an empty array) when the gate is platform-`admin`-only.
 * For example: chat moderation is platform-wide and uses `requireAdmin`
 * server-side, so its client gate would be `useArcheAdmin()` (or simply
 * a direct `roles.includes('admin')` check — both are equivalent for the
 * platform-admin-only case).
 */
export function useArcheAdmin(...domainRoles: readonly string[]): boolean {
  const { user } = useAuth();
  // Stringify the role list so the memo only re-runs when it actually
  // changes — call sites usually pass literal arguments that are
  // referentially fresh on every render.
  const domainRolesKey = domainRoles.join('|');
  return useMemo(() => {
    if (!user?.roles || user.roles.length === 0) return false;
    return user.roles.some((r) => r === 'admin' || domainRoles.includes(r));
    // Memo dep is the joined key (stable across renders) rather than the
    // array literal — equivalent contents, fewer recomputes when call
    // sites pass fresh array literals every render.
  }, [user?.roles, domainRolesKey]);
}
